import { apiClient } from './apiClient';

export interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  verified_users: number;
  unverified_users: number;
  role_distribution: Record<string, number>;
}

export interface User {
  id: number;
  email: string;
  username: string;
  nickname?: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  is_email_verified: boolean;
  profile_picture_url?: string;
  phone_number?: string;
  status: string;
  effective_permissions?: string[];
  roles: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  created_at: string;
  updated_at?: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
}

export interface GetUsersParams {
  skip?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

export interface GetUsersResponse {
  users: User[];
  totalCount: number;
}

export interface TechnicalDepartmentStats {
  department: string;
  total_users: number;
  roles_included: string[];
}

export const userService = {
  async getUserStats(): Promise<UserStats> {
    return apiClient.get('/users/stats/overview');
  },

  async getUsers(params: GetUsersParams = {}): Promise<GetUsersResponse> {
    const query = new URLSearchParams();
    if (params.skip !== undefined) query.append('skip', params.skip.toString());
    if (params.limit !== undefined) query.append('limit', params.limit.toString());
    if (params.search) query.append('search', params.search);
    if (params.role) query.append('role', params.role);
    if (params.status) query.append('status', params.status);
    
    const data = await apiClient.get(`/users/?${query.toString()}`);
    
    // Normalize response
    if (Array.isArray(data)) {
      return { users: data, totalCount: data.length };
    } else if (data.items && typeof data.total === 'number') {
      return { users: data.items, totalCount: data.total };
    } else if (data.users && typeof data.totalCount === 'number') {
      return data;
    }
    return { users: [], totalCount: 0 };
  },

  async getUserById(userId: number): Promise<User> {
    return apiClient.get(`/users/${userId}`);
  },

  async getRoles(): Promise<Role[]> {
    return apiClient.get('/roles/');
  },

  async deleteUser(userId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}`);
  },

  async updateUserStatus(userId: number, status: string, reason: string): Promise<User> {
    const response = await apiClient.patch<User>(`/users/${userId}/status`, { status, reason });
    // Backend returns User directly, apiClient.patch returns response.data which is User
    // Handle both cases: if response has .data property (ApiResponse), use it; otherwise use response directly
    if (response && typeof response === 'object' && 'data' in response) {
      return (response as any).data;
    }
    return response as unknown as User;
  },

  async assignRoleToUser(userId: number, roleId: number): Promise<User> {
    return apiClient.post(`/roles/assign/${userId}/${roleId}`);
  },

  async removeRoleFromUser(userId: number, roleId: number): Promise<User> {
    return apiClient.post(`/roles/remove/${userId}/${roleId}`);
  },

  async resendVerificationEmail(userId: number): Promise<void> {
    return apiClient.post(`/users/resend-verification/${userId}`);
  },

  async requestPasswordReset(userId: number): Promise<void> {
    return apiClient.post(`/users/request-password-reset/${userId}`);
  },

  async createUser(userData: any): Promise<User> {
    return apiClient.post('/users/', userData);
  },

  async getTechnicalDepartmentCount(): Promise<TechnicalDepartmentStats> {
    return apiClient.get('/users/technical-department-count');
  },

  async getTechnicalDepartmentUsers(params: GetUsersParams = {}): Promise<GetUsersResponse> {
    const query = new URLSearchParams();
    if (params.skip !== undefined) query.append('skip', params.skip.toString());
    if (params.limit !== undefined) query.append('limit', params.limit.toString());
    if (params.search) query.append('search', params.search);
    
    const data = await apiClient.get(`/users/technical-department?${query.toString()}`);
    
    // Normalize response to match GetUsersResponse format
    return {
      users: data.users || [],
      totalCount: data.total || 0
    };
  },
};
